import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/Select';
import type { ServiceOffering } from '../../../core/models/ServiceOffering';
import { ServiceUnit } from '../../../core/models/ServiceOffering';

interface ServiceDialogProps {
  open: boolean;
  onClose: () => void;
  service?: Partial<ServiceOffering>;
  onSubmit: (data: any) => Promise<void>;
}

export const ServiceDialog: React.FC<ServiceDialogProps> = ({
  open,
  onClose,
  service,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name: service?.name || '',
      description: service?.description || '',
      defaultRate: service?.defaultRate || 0,
      unit: service?.unit || ServiceUnit.MONTH,
      category: service?.category || 'general',
      isActive: service?.isActive ?? true,
    },
  });

  const unitValue = watch('unit');
  const categoryValue = watch('category');
  
  // Register unit and category fields to ensure they're included in form data
  React.useEffect(() => {
    register('unit', { required: 'Unit is required' });
    register('category');
  }, [register]);

  // Reset form when service prop changes (switching between create/edit modes)
  React.useEffect(() => {
    if (open) {
      reset({
        name: service?.name || '',
        description: service?.description || '',
        defaultRate: service?.defaultRate || 0,
        unit: service?.unit || ServiceUnit.MONTH,
        category: service?.category || 'general',
        isActive: service?.isActive ?? true,
      });
    }
  }, [service, open, reset]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{service ? 'Edit Service' : 'Create Service'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Service Name"
            {...register('name', { required: 'Name is required' })}
            error={errors.name?.message}
          />
          <Textarea
            label="Description"
            {...register('description')}
            rows={3}
          />
          <div className="space-y-2">
            <label className="text-sm font-medium">Category</label>
            <Select
              value={categoryValue}
              onValueChange={(value) => setValue('category', value, { shouldValidate: true })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="consulting">Consulting</SelectItem>
                <SelectItem value="development">Development</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="support">Support</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-sm text-red-600">{errors.category.message}</p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="Default Rate"
              type="number"
              {...register('defaultRate', { required: 'Default rate is required', valueAsNumber: true })}
              error={errors.defaultRate?.message}
            />
            <div className="space-y-2">
              <label className="text-sm font-medium">Unit</label>
              <Select
                value={unitValue}
                onValueChange={(value) => setValue('unit', value as ServiceUnit, { shouldValidate: true })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ServiceUnit.HOUR}>Hour</SelectItem>
                  <SelectItem value={ServiceUnit.DAY}>Day</SelectItem>
                  <SelectItem value={ServiceUnit.WEEK}>Week</SelectItem>
                  <SelectItem value={ServiceUnit.MONTH}>Month</SelectItem>
                  <SelectItem value={ServiceUnit.PROJECT}>Project</SelectItem>
                </SelectContent>
              </Select>
              {errors.unit && (
                <p className="text-sm text-red-600">{errors.unit.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              checked={watch('isActive')}
              onCheckedChange={(checked) => setValue('isActive', checked === true, { shouldValidate: true })}
            />
            <label className="text-sm font-medium">Active Service</label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {service ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
